import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Link2, Copy, Mail } from "lucide-react";
import { API } from "../../data/site";
import { inputCls, Field, AdminButton } from "../ui";

const STATUS_TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

const STATUS_STYLES = {
  pending: "bg-[#A37C45] text-white",
  approved: "bg-[#4E735B] text-white",
  rejected: "bg-[#9C4A3C] text-white",
};

const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`font-mono text-[0.58rem] tracking-[0.15em] uppercase font-bold px-2.5 py-1 ${STATUS_STYLES[status] || "bg-[#4B4B4B] text-white"}`}>
    {status || "approved"}
  </span>
);

const RatingDiamonds = ({ rating }) => {
  if (!rating) return null;
  return (
    <span className="inline-flex items-center gap-1" aria-label={`${rating} out of 5`}>
      {Array.from({ length: rating }).map((_, i) => (
        <span key={i} className="w-1.5 h-1.5 rotate-45 bg-[#8C6A4F]" />
      ))}
    </span>
  );
};

const EMPTY_FORM = {
  name: "", email: "", project: "", rating: "", date: "",
  text: "", status: "approved", featured: false, published: true,
};

const toForm = (t) =>
  t
    ? {
        name: t.name || "", email: t.email || "", project: t.project || "",
        rating: t.rating ?? "", date: t.date || "", text: t.text || "",
        status: t.status || "approved", featured: !!t.featured, published: t.published !== false,
      }
    : { ...EMPTY_FORM };

const RequestLinkCard = () => {
  const [client, setClient] = useState("");
  const [project, setProject] = useState("");

  const link = useMemo(() => {
    const base = `${window.location.origin}/testimonial`;
    const q = new URLSearchParams();
    if (client.trim()) q.set("client", client.trim());
    if (project.trim()) q.set("project", project.trim());
    const qs = q.toString();
    return qs ? `${base}?${qs}` : base;
  }, [client, project]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied — send it to your client");
    } catch {
      toast.error("Copy failed — select the link and copy it manually");
    }
  };

  return (
    <div className="border border-[rgba(26,26,26,0.15)] bg-white/50 p-6 md:p-8 mb-10" data-testid="request-link-card">
      <div className="flex items-center gap-3 mb-1">
        <Link2 size={15} className="text-[#8C6A4F]" />
        <h2 className="font-serif text-2xl">Request a Testimonial</h2>
      </div>
      <p className="text-sm text-[#5C5A56] mb-6 max-w-2xl">
        Send this private link to a homeowner after their final walkthrough. Add their name and project to pre-fill
        the form for them.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Field label="Client Name (optional)" testid="request-field-client">
          <input value={client} onChange={(e) => setClient(e.target.value)} className={inputCls} placeholder="e.g. John & Sarah Miller" data-testid="request-client-input" />
        </Field>
        <Field label="Project / Home (optional)" testid="request-field-project">
          <input value={project} onChange={(e) => setProject(e.target.value)} className={inputCls} placeholder="e.g. The Willowbrook" data-testid="request-project-input" />
        </Field>
      </div>
      <div className="flex flex-col md:flex-row gap-3 md:items-stretch">
        <code className="flex-1 bg-[#F7F5F2] border border-[#1A1A1A]/15 px-4 py-3 text-xs font-mono text-[#5C5A56] break-all" data-testid="request-link-output">
          {link}
        </code>
        <AdminButton onClick={copy} testid="request-copy-btn">
          <span className="inline-flex items-center gap-2"><Copy size={13} /> Copy Link</span>
        </AdminButton>
      </div>
    </div>
  );
};

const TestimonialForm = ({ initial, title, onSave, onCancel, saving }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="mb-10 border border-[rgba(26,26,26,0.15)] p-6 md:p-8 bg-white/50">
      <h2 className="font-serif text-2xl mb-6">{title}</h2>
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="testimonial-form-admin">
        <Field label="Customer Name" testid="field-name">
          <input required value={f.name} onChange={set("name")} className={inputCls} data-testid="testimonial-form-name" />
        </Field>
        <Field label="Email (private)" testid="field-email">
          <input type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="testimonial-form-email" />
        </Field>
        <Field label="Project / Home" testid="field-project">
          <input value={f.project} onChange={set("project")} className={inputCls} data-testid="testimonial-form-project" />
        </Field>
        <Field label="Rating (1–5, optional)" testid="field-rating">
          <input type="number" min="1" max="5" value={f.rating} onChange={set("rating")} className={inputCls} data-testid="testimonial-form-rating" />
        </Field>
        <Field label="Date (e.g. July 2026)" testid="field-date">
          <input value={f.date} onChange={set("date")} className={inputCls} data-testid="testimonial-form-date" />
        </Field>
        <Field label="Approval Status" testid="field-status">
          <select value={f.status} onChange={set("status")} className={inputCls} data-testid="testimonial-form-status">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </Field>
        <Field label="Testimonial" span={3} testid="field-text">
          <textarea required rows={4} value={f.text} onChange={set("text")} className={inputCls} data-testid="testimonial-form-text" />
        </Field>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={!!f.featured} onChange={set("featured")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="testimonial-form-featured" />
          <span className="eyebrow text-[#5C5A56]">Featured</span>
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={!!f.published} onChange={set("published")} className="w-4 h-4 accent-[#8C6A4F]" data-testid="testimonial-form-published" />
          <span className="eyebrow text-[#5C5A56]">Published (live)</span>
        </label>
        <div className="md:col-span-3 flex gap-3 pt-2">
          <AdminButton primary testid="testimonial-form-save" disabled={saving}>{saving ? "Saving…" : "Save"}</AdminButton>
          <AdminButton onClick={onCancel} testid="testimonial-form-cancel">Cancel</AdminButton>
        </div>
      </form>
    </div>
  );
};

const TestimonialsPanel = ({ openNewKey }) => {
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("pending");
  const { data: items = [], refetch } = useQuery({
    queryKey: ["testimonials-admin"],
    queryFn: async () => (await axios.get(`${API}/testimonials-admin`, { withCredentials: true })).data,
  });

  useEffect(() => { if (openNewKey) setEditing("new"); }, [openNewKey]);

  const counts = useMemo(() => ({
    pending: items.filter((t) => t.status === "pending").length,
    approved: items.filter((t) => !t.status || t.status === "approved").length,
    rejected: items.filter((t) => t.status === "rejected").length,
    all: items.length,
  }), [items]);

  const visible = filter === "all" ? items : items.filter((t) => (t.status || "approved") === filter);

  const save = async (f) => {
    const payload = {
      name: f.name, email: f.email, project: f.project,
      rating: f.rating === "" ? null : Number(f.rating),
      date: f.date, text: f.text, status: f.status,
      permission_granted: true,
      featured: !!f.featured, published: !!f.published,
      photo: editing?.photo || "",
    };
    setSaving(true);
    try {
      if (editing === "new") await axios.post(`${API}/testimonials`, payload, { withCredentials: true });
      else await axios.put(`${API}/testimonials/${editing.id}`, payload, { withCredentials: true });
      toast.success("Testimonial saved");
      setEditing(null);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (t, status) => {
    await axios.patch(`${API}/testimonials/${t.id}/status`, { status }, { withCredentials: true });
    toast.success(status === "approved" ? "Approved — now live on the website" : status === "rejected" ? "Testimonial rejected" : "Moved to pending");
    refetch();
  };

  const remove = async (t) => {
    if (!window.confirm(`Permanently delete the testimonial from "${t.name}"? This cannot be undone.`)) return;
    await axios.delete(`${API}/testimonials/${t.id}`, { withCredentials: true });
    toast.success("Testimonial deleted");
    refetch();
  };

  return (
    <div data-testid="testimonials-panel">
      {editing === null && (
        <>
          <RequestLinkCard />
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter testimonials">
              {STATUS_TABS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setFilter(s.id)}
                  data-testid={`filter-${s.id}`}
                  className={`font-mono text-[0.62rem] tracking-[0.15em] uppercase font-bold px-4 py-2 border transition-colors duration-300 ${
                    filter === s.id ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]" : "border-[#1A1A1A]/20 text-[#5C5A56] hover:border-[#8C6A4F]"
                  }`}
                >
                  {s.label} ({counts[s.id]})
                </button>
              ))}
            </div>
            <AdminButton primary onClick={() => setEditing("new")} testid="add-testimonial-btn">
              <span className="inline-flex items-center gap-2"><Plus size={14} /> Add Testimonial</span>
            </AdminButton>
          </div>
        </>
      )}

      {editing !== null && (
        <TestimonialForm
          initial={toForm(editing === "new" ? null : editing)}
          title={editing === "new" ? "Add Testimonial" : `Edit — ${editing.name}`}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}

      {editing === null && (
        <div className="flex flex-col gap-4" data-testid="testimonials-list">
          {visible.map((t) => (
            <article key={t.id} className="border border-[rgba(26,26,26,0.15)] bg-white/50 p-6" data-testid={`testimonial-card-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-serif text-xl">{t.name}</h3>
                    <StatusBadge status={t.status || "approved"} testid={`testimonial-status-${t.id}`} />
                    <RatingDiamonds rating={t.rating} />
                    {t.featured && <span className="font-mono text-[0.55rem] tracking-[0.15em] uppercase text-[#8C6A4F]">Featured</span>}
                    {t.published === false && <span className="font-mono text-[0.55rem] tracking-[0.15em] uppercase text-[#A3A09B]">Unpublished</span>}
                  </div>
                  <p className="mt-1 text-xs text-[#A3A09B] font-mono tracking-wider">
                    {[t.project, t.date].filter(Boolean).join(" — ") || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {t.status !== "approved" && (
                    <button onClick={() => setStatus(t, "approved")} className="p-2 inline-flex items-center gap-1.5 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#4E735B] hover:underline" data-testid={`approve-${t.id}`}>
                      <Check size={14} /> Approve
                    </button>
                  )}
                  {t.status !== "rejected" && (
                    <button onClick={() => setStatus(t, "rejected")} className="p-2 inline-flex items-center gap-1.5 font-mono text-[0.58rem] tracking-[0.12em] uppercase font-bold text-[#9C4A3C] hover:underline" data-testid={`reject-${t.id}`}>
                      <X size={14} /> Reject
                    </button>
                  )}
                  <button onClick={() => setEditing(t)} className="p-2 hover:text-[#8C6A4F] transition-colors" data-testid={`edit-${t.id}`} aria-label="Edit"><Pencil size={15} /></button>
                  <button onClick={() => remove(t)} className="p-2 hover:text-red-600 transition-colors" data-testid={`delete-${t.id}`} aria-label="Delete"><Trash2 size={15} /></button>
                </div>
              </div>
              <blockquote className="mt-4 font-serif italic text-lg leading-snug text-[#1A1A1A] border-l-2 border-[#8C6A4F] pl-5">
                "{t.text}"
              </blockquote>
              {t.email && (
                <p className="mt-3 inline-flex items-center gap-2 text-xs text-[#5C5A56]" data-testid={`email-${t.id}`}>
                  <Mail size={12} className="text-[#8C6A4F]" /> {t.email} <span className="text-[#A3A09B]">(private)</span>
                </p>
              )}
            </article>
          ))}
          {visible.length === 0 && (
            <p className="py-10 text-[#A3A09B] font-mono text-xs tracking-[0.2em] uppercase" data-testid="testimonials-empty">
              {filter === "pending" ? "No pending testimonials — share the request link above to collect client stories." : "No testimonials in this view."}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TestimonialsPanel;
